import type { Locale } from "@/i18n/locales";

function copy<T>(nl: T, en: T, de: T, fr: T, es: T): Record<Locale, T> {
  return { nl, en, de, fr, es };
}

const nl = {
  home: {
    title: "Vaxy – digitale vaccinatie-app | Gratis iOS & Android",
    description:
      "Gratis vaccinatieboekje voor het hele gezin. Bewaar vaccinaties, krijg herinneringen en reisadvies. Download Vaxy voor iOS en Android.",
  },
  about: {
    title: "Over ons | Vaxy",
    description:
      "Het verhaal van Vaxy, opgericht door Dr. Marco Goeijenbier en Pim Bruijnes. Ontmoet de mensen achter het digitale vaccinatieboekje.",
  },
  media: {
    title: "Media | Vaxy",
    description:
      "Bekijk Vaxy in de media en ontdek hoe de app mensen helpt hun vaccinaties te beheren.",
  },
  community: {
    title: "Community | Vaxy",
    description:
      "Sluit je aan bij een community die gelooft in vaccinatie, preventie en betere gezondheid voor iedereen.",
  },
  app: {
    title: "Download Vaxy – vaccinatie-app | iOS & Android",
    description:
      "Download de gratis vaccinatie-app voor gezinnen: reisvaccins, herinneringen en veilige PDF-overzichten. Beschikbaar in de App Store en Google Play.",
  },
  login: {
    title: "Inloggen | Vaxy",
    description: "Log in op Vaxy met Facebook, Apple, Google of e-mail.",
  },
  register: {
    title: "Registreren | Vaxy",
    description:
      "Maak een Vaxy-account aan met Facebook, Apple, Google of e-mail.",
  },
  account: {
    title: "Mijn overzicht | Vaxy",
    description:
      "Bekijk je Vaxy-account in de browser met dezelfde onderdelen als in de app.",
  },
  travel: {
    title: "Reisvaccinaties per land | Vaxy",
    description:
      "Vaccinatieadvies per bestemming, gebaseerd op LCR-richtlijnen. Kies een land en bewaar je reisvaccins in Vaxy.",
  },
  travelRegion: {
    title: "Vaccinaties {region} | Vaxy",
    description:
      "Vaccinatieadvies voor {region}, met LCR-informatie per land. Bereid je reis voor en bewaar je vaccins in Vaxy.",
  },
  travelCountry: {
    title: "Vaccinaties {country} | Vaxy",
    description:
      "Welke vaccinaties heb je nodig voor {country}? LCR-advies over {vaccines}. Bewaar je overzicht in Vaxy.",
  },
  travelDiseases: {
    title: "Infectieziekten en reisvaccins | Vaxy",
    description:
      "LCR-advies per infectieziekte: voor welke landen vaccinatie of extra voorzorg geldt, met links naar het landadvies.",
  },
  travelDisease: {
    title: "{name} | Reisvaccinaties | Vaxy",
    description:
      "{what} Het LCR noemt {name} voor {count} bestemmingen. Bewaar je vaccins in Vaxy.",
  },
  blog: {
    title: "Artikelen | Vaxy",
    description:
      "Praktische artikelen over vaccinaties, reizen en gezinsgezondheid.",
  },
  blogPost: {
    title: "{title} | Vaxy",
    description:
      "Lees dit artikel van Vaxy over vaccinaties, reizen en gezondheid.",
  },
  privacy: {
    title: "Privacy | Vaxy",
    description:
      "Lees hoe Vaxy met persoonsgegevens en gezondheidsgegevens omgaat.",
  },
  cookies: {
    title: "Cookies | Vaxy",
    description:
      "Lees welke cookies Vaxy gebruikt en hoe je je voorkeuren beheert.",
  },
  terms: {
    title: "Voorwaarden | Vaxy",
    description: "Lees de gebruiksvoorwaarden voor Vaxy.",
  },
  faq: {
    title: "Veelgestelde vragen | Vaxy",
    description:
      "Antwoorden op veelgestelde vragen over Vaxy en het digitale vaccinatieboekje.",
  },
  contact: {
    title: "Contact | Vaxy",
    description:
      "Neem contact op met Vaxy voor vragen over de app of het digitale vaccinatieboekje.",
  },
  deleteAccount: {
    title: "Account verwijderen | Vaxy",
    description:
      "Verwijder je Vaxy-account in de app of dien een verzoek in om je account en persoonsgegevens te laten wissen.",
  },
  developers: {
    title: "Content API | Vaxy",
    description:
      "Documentatie voor de statische Vaxy Content API: reisvaccinatie-advies, infectieziekten en bronnen als JSON.",
  },
};

export type MetaCopy = typeof nl;

const en: MetaCopy = {
  home: {
    title: "Vaxy – digital vaccination app | Free iOS & Android",
    description:
      "Free digital vaccination record for the whole family. Store vaccines, get reminders and travel health advice. Download Vaxy for iOS and Android.",
  },
  about: {
    title: "About us | Vaxy",
    description:
      "The story of Vaxy, founded by Dr. Marco Goeijenbier and Pim Bruijnes. Meet the people behind the digital vaccination booklet.",
  },
  media: {
    title: "Media | Vaxy",
    description:
      "See Vaxy in the media and discover how the app helps people manage vaccinations.",
  },
  community: {
    title: "Community | Vaxy",
    description:
      "Join a community that believes in vaccination, prevention and better health for everyone.",
  },
  app: {
    title: "Download Vaxy – vaccination app | iOS & Android",
    description:
      "Download the free vaccination app for families: travel vaccines, reminders and secure PDF summaries. Available on the App Store and Google Play.",
  },
  login: {
    title: "Log in | Vaxy",
    description: "Log in to Vaxy with Facebook, Apple, Google or email.",
  },
  register: {
    title: "Register | Vaxy",
    description:
      "Create a Vaxy account with Facebook, Apple, Google or email.",
  },
  account: {
    title: "My overview | Vaxy",
    description:
      "View your Vaxy account in the browser with the same sections as in the app.",
  },
  travel: {
    title: "Travel vaccinations by country | Vaxy",
    description:
      "Destination-based vaccination advice from LCR guidelines. Choose a country and store travel vaccines in Vaxy.",
  },
  travelRegion: {
    title: "Vaccinations {region} | Vaxy",
    description:
      "Vaccination advice for {region}, with LCR information per country. Prepare your trip and store vaccines in Vaxy.",
  },
  travelCountry: {
    title: "Vaccinations {country} | Vaxy",
    description:
      "Which vaccinations do you need for {country}? LCR advice covering {vaccines}. Keep your record in Vaxy.",
  },
  travelDiseases: {
    title: "Infectious diseases and travel vaccines | Vaxy",
    description:
      "LCR advice per infectious disease: which countries need vaccination or extra precautions, with links to country pages.",
  },
  travelDisease: {
    title: "{name} | Travel vaccines | Vaxy",
    description:
      "{what} LCR mentions {name} for {count} destinations. Store your vaccines in Vaxy.",
  },
  blog: {
    title: "Articles | Vaxy",
    description:
      "Practical articles about vaccinations, travel and family health.",
  },
  blogPost: {
    title: "{title} | Vaxy",
    description:
      "Read this Vaxy article about vaccinations, travel and health.",
  },
  privacy: {
    title: "Privacy | Vaxy",
    description: "Read how Vaxy handles personal and health data.",
  },
  cookies: {
    title: "Cookies | Vaxy",
    description:
      "Read which cookies Vaxy uses and how to manage your preferences.",
  },
  terms: {
    title: "Terms | Vaxy",
    description: "Read the terms of use for Vaxy.",
  },
  faq: {
    title: "FAQ | Vaxy",
    description:
      "Answers to common questions about Vaxy and the digital vaccination booklet.",
  },
  contact: {
    title: "Contact | Vaxy",
    description:
      "Contact Vaxy with questions about the app or digital vaccination booklet.",
  },
  deleteAccount: {
    title: "Delete account | Vaxy",
    description:
      "Delete your Vaxy account in the app or submit a request to erase your account and personal data.",
  },
  developers: {
    title: "Content API | Vaxy",
    description:
      "Documentation for the static Vaxy Content API: travel vaccination advice, diseases and sources as JSON.",
  },
};

const de: MetaCopy = {
  home: {
    title: "Vaxy – digitale Impf-App | Kostenlos iOS & Android",
    description:
      "Kostenloser digitaler Impfpass für die ganze Familie. Speichere Impfungen, erhalte Erinnerungen und Reiseberatung. Vaxy für iOS und Android laden.",
  },
  about: {
    title: "Über uns | Vaxy",
    description:
      "Die Geschichte von Vaxy, gegründet von Dr. Marco Goeijenbier und Pim Bruijnes. Lernen Sie die Menschen hinter dem digitalen Impfheft kennen.",
  },
  media: {
    title: "Medien | Vaxy",
    description:
      "Sieh Vaxy in den Medien und entdecke, wie die App Menschen beim Verwalten ihrer Impfungen hilft.",
  },
  community: {
    title: "Community | Vaxy",
    description:
      "Werde Teil einer Community, die an Impfungen, Vorbeugung und bessere Gesundheit für alle glaubt.",
  },
  app: {
    title: "Vaxy herunterladen – Impf-App | iOS & Android",
    description:
      "Lade die kostenlose Impf-App für Familien: Reiseimpfungen, Erinnerungen und sichere PDF-Übersichten. App Store und Google Play.",
  },
  login: {
    title: "Anmelden | Vaxy",
    description:
      "Melde dich bei Vaxy mit Facebook, Apple, Google oder E-Mail an.",
  },
  register: {
    title: "Registrieren | Vaxy",
    description:
      "Erstelle ein Vaxy-Konto mit Facebook, Apple, Google oder E-Mail.",
  },
  account: {
    title: "Meine Übersicht | Vaxy",
    description:
      "Sieh dein Vaxy-Konto im Browser mit denselben Bereichen wie in der App.",
  },
  travel: {
    title: "Reiseimpfungen nach Land | Vaxy",
    description:
      "Impfberatung nach Reiseziel auf Basis der LCR-Richtlinien. Wähle ein Land und speichere Reiseimpfungen in Vaxy.",
  },
  travelRegion: {
    title: "Impfungen {region} | Vaxy",
    description:
      "Impfberatung für {region}, mit LCR-Informationen pro Land. Bereite deine Reise vor und speichere Impfungen in Vaxy.",
  },
  travelCountry: {
    title: "Impfungen {country} | Vaxy",
    description:
      "Welche Impfungen brauchst du für {country}? LCR-Beratung zu {vaccines}. Speichere deinen Überblick in Vaxy.",
  },
  travelDiseases: {
    title: "Infektionskrankheiten und Reiseimpfungen | Vaxy",
    description:
      "LCR-Beratung je Infektionskrankheit: für welche Länder Impfung oder extra Vorsicht gilt, mit Links zum Länderberatung.",
  },
  travelDisease: {
    title: "{name} | Reiseimpfungen | Vaxy",
    description:
      "LCR-Beratung zu {name} für Reisende. Siehe für welche {count} Reiseziele das gilt und speichere Impfungen in Vaxy.",
  },
  blog: {
    title: "Artikel | Vaxy",
    description:
      "Praktische Artikel über Impfungen, Reisen und Familiengesundheit.",
  },
  blogPost: {
    title: "{title} | Vaxy",
    description:
      "Lies diesen Vaxy Artikel über Impfungen, Reisen und Gesundheit.",
  },
  privacy: {
    title: "Datenschutz | Vaxy",
    description:
      "Lies, wie Vaxy mit persönlichen Daten und Gesundheitsdaten umgeht.",
  },
  cookies: {
    title: "Cookies | Vaxy",
    description:
      "Lies, welche Cookies Vaxy nutzt und wie du deine Einstellungen verwaltest.",
  },
  terms: {
    title: "Bedingungen | Vaxy",
    description: "Lies die Nutzungsbedingungen für Vaxy.",
  },
  faq: {
    title: "FAQ | Vaxy",
    description:
      "Antworten auf häufige Fragen zu Vaxy und dem digitalen Impfpass.",
  },
  contact: {
    title: "Kontakt | Vaxy",
    description:
      "Kontaktiere Vaxy bei Fragen zur App oder zum digitalen Impfpass.",
  },
  deleteAccount: {
    title: "Konto löschen | Vaxy",
    description:
      "Lösche dein Vaxy-Konto in der App oder sende eine Anfrage zur Löschung deines Kontos und deiner personenbezogenen Daten.",
  },
  developers: {
    title: "Content API | Vaxy",
    description:
      "Dokumentation der statischen Vaxy Content API: Reiseimpfberatung, Krankheiten und Quellen als JSON.",
  },
};

const fr: MetaCopy = {
  home: {
    title: "Vaxy – app vaccinale numérique | Gratuite iOS & Android",
    description:
      "Carnet de vaccination numérique gratuit pour toute la famille. Rappels et conseils voyage. Téléchargez Vaxy sur iOS et Android.",
  },
  about: {
    title: "À propos | Vaxy",
    description:
      "L'histoire de Vaxy, fondée par le Dr. Marco Goeijenbier et Pim Bruijnes. Découvrez les personnes derrière le carnet de vaccination numérique.",
  },
  media: {
    title: "Médias | Vaxy",
    description:
      "Découvrez Vaxy dans les médias et comment l'application aide à gérer les vaccinations.",
  },
  community: {
    title: "Communauté | Vaxy",
    description:
      "Rejoignez une communauté qui croit en la vaccination, la prévention et une meilleure santé pour tous.",
  },
  app: {
    title: "Télécharger Vaxy – app vaccinale | iOS & Android",
    description:
      "Téléchargez l'app vaccinale gratuite pour familles : vaccins voyage, rappels et PDF sécurisés. App Store et Google Play.",
  },
  login: {
    title: "Connexion | Vaxy",
    description:
      "Connectez-vous à Vaxy avec Facebook, Apple, Google ou e-mail.",
  },
  register: {
    title: "Inscription | Vaxy",
    description:
      "Créez un compte Vaxy avec Facebook, Apple, Google ou e-mail.",
  },
  account: {
    title: "Mon aperçu | Vaxy",
    description:
      "Consultez votre compte Vaxy dans le navigateur avec les mêmes sections que dans l'app.",
  },
  travel: {
    title: "Vaccins de voyage par pays | Vaxy",
    description:
      "Conseils vaccinaux par destination selon les directives LCR. Choisissez un pays et conservez vos vaccins de voyage dans Vaxy.",
  },
  travelRegion: {
    title: "Vaccins {region} | Vaxy",
    description:
      "Conseils vaccinaux pour {region}, avec les informations LCR par pays. Préparez votre voyage et conservez vos vaccins dans Vaxy.",
  },
  travelCountry: {
    title: "Vaccins {country} | Vaxy",
    description:
      "Quels vaccins pour {country} ? Conseils LCR concernant {vaccines}. Conservez votre carnet dans Vaxy.",
  },
  travelDiseases: {
    title: "Maladies infectieuses et vaccins de voyage | Vaxy",
    description:
      "Conseils LCR par maladie : dans quels pays une vaccination ou des précautions extra s'appliquent, avec liens vers les fiches pays.",
  },
  travelDisease: {
    title: "{name} | Vaccins de voyage | Vaxy",
    description:
      "Conseils LCR sur {name} pour les voyageurs. Voir pour quelles {count} destinations cela compte et conservez vos vaccins dans Vaxy.",
  },
  blog: {
    title: "Articles | Vaxy",
    description:
      "Articles pratiques sur les vaccinations, les voyages et la santé familiale.",
  },
  blogPost: {
    title: "{title} | Vaxy",
    description:
      "Lisez cet article Vaxy sur les vaccinations, les voyages et la santé.",
  },
  privacy: {
    title: "Confidentialité | Vaxy",
    description:
      "Découvrez comment Vaxy traite les données personnelles et de santé.",
  },
  cookies: {
    title: "Cookies | Vaxy",
    description:
      "Découvrez les cookies utilisés par Vaxy et la gestion de vos préférences.",
  },
  terms: {
    title: "Conditions | Vaxy",
    description: "Lisez les conditions d'utilisation de Vaxy.",
  },
  faq: {
    title: "FAQ | Vaxy",
    description:
      "Réponses aux questions fréquentes sur Vaxy et le carnet vaccinal numérique.",
  },
  contact: {
    title: "Contact | Vaxy",
    description:
      "Contactez Vaxy pour toute question sur l'application ou le carnet vaccinal numérique.",
  },
  deleteAccount: {
    title: "Supprimer mon compte | Vaxy",
    description:
      "Supprimez votre compte Vaxy dans l'application ou envoyez une demande d'effacement de votre compte et de vos données personnelles.",
  },
  developers: {
    title: "API Content | Vaxy",
    description:
      "Documentation de l'API Content statique Vaxy : conseils de voyage, maladies et sources en JSON.",
  },
};

const es: MetaCopy = {
  home: {
    title: "Vaxy – app de vacunación digital | Gratis iOS y Android",
    description:
      "Cartilla de vacunación digital gratis para toda la familia. Recordatorios y consejos de viaje. Descarga Vaxy en iOS y Android.",
  },
  about: {
    title: "Sobre nosotros | Vaxy",
    description:
      "La historia de Vaxy, fundada por el Dr. Marco Goeijenbier y Pim Bruijnes. Conoce a las personas detrás de la cartilla de vacunación digital.",
  },
  media: {
    title: "Medios | Vaxy",
    description:
      "Ve Vaxy en los medios y descubre cómo la app ayuda a gestionar vacunaciones.",
  },
  community: {
    title: "Comunidad | Vaxy",
    description:
      "Únete a una comunidad que cree en la vacunación, la prevención y una mejor salud para todos.",
  },
  app: {
    title: "Descargar Vaxy – app de vacunas | iOS y Android",
    description:
      "Descarga la app de vacunas gratis para familias: vacunas de viaje, recordatorios y PDF seguros. App Store y Google Play.",
  },
  login: {
    title: "Iniciar sesión | Vaxy",
    description:
      "Inicia sesión en Vaxy con Facebook, Apple, Google o correo.",
  },
  register: {
    title: "Registrarse | Vaxy",
    description:
      "Crea una cuenta de Vaxy con Facebook, Apple, Google o correo.",
  },
  account: {
    title: "Mi resumen | Vaxy",
    description:
      "Consulta tu cuenta de Vaxy en el navegador con las mismas secciones que en la app.",
  },
  travel: {
    title: "Vacunas de viaje por país | Vaxy",
    description:
      "Consejos de vacunación por destino según las directrices LCR. Elige un país y guarda tus vacunas de viaje en Vaxy.",
  },
  travelRegion: {
    title: "Vacunas {region} | Vaxy",
    description:
      "Consejos de vacunación para {region}, con información LCR por país. Prepara tu viaje y guarda tus vacunas en Vaxy.",
  },
  travelCountry: {
    title: "Vacunas {country} | Vaxy",
    description:
      "¿Qué vacunas necesitas para {country}? Consejo LCR sobre {vaccines}. Guarda tu registro en Vaxy.",
  },
  travelDiseases: {
    title: "Enfermedades infecciosas y vacunas de viaje | Vaxy",
    description:
      "Consejo LCR por enfermedad: en qué países aplica vacunación o precaución extra, con enlaces a las fichas de país.",
  },
  travelDisease: {
    title: "{name} | Vacunas de viaje | Vaxy",
    description:
      "Consejo LCR sobre {name} para viajeros. Mira para qué {count} destinos aplica y guarda tus vacunas en Vaxy.",
  },
  blog: {
    title: "Artículos | Vaxy",
    description: "Artículos prácticos sobre vacunas, viajes y salud familiar.",
  },
  blogPost: {
    title: "{title} | Vaxy",
    description: "Lee este artículo de Vaxy sobre vacunas, viajes y salud.",
  },
  privacy: {
    title: "Privacidad | Vaxy",
    description: "Lee cómo Vaxy trata los datos personales y de salud.",
  },
  cookies: {
    title: "Cookies | Vaxy",
    description: "Lee qué cookies usa Vaxy y cómo gestionar tus preferencias.",
  },
  terms: {
    title: "Condiciones | Vaxy",
    description: "Lee las condiciones de uso de Vaxy.",
  },
  faq: {
    title: "Preguntas frecuentes | Vaxy",
    description:
      "Respuestas a preguntas frecuentes sobre Vaxy y la cartilla digital de vacunación.",
  },
  contact: {
    title: "Contacto | Vaxy",
    description:
      "Contacta con Vaxy para preguntas sobre la app o la cartilla digital de vacunación.",
  },
  deleteAccount: {
    title: "Eliminar cuenta | Vaxy",
    description:
      "Elimina tu cuenta de Vaxy en la app o envía una solicitud para borrar tu cuenta y tus datos personales.",
  },
  developers: {
    title: "API de contenido | Vaxy",
    description:
      "Documentación de la API de contenido estática de Vaxy: consejos de viaje, enfermedades y fuentes en JSON.",
  },
};

export const meta: Record<Locale, MetaCopy> = copy(nl, en, de, fr, es);
